import React from 'react';
import {
	Box,
	Typography,
	Paper,
	Chip,
	Collapse,
	IconButton,
	Divider,
	ListItem,
} from '@mui/material';
import {
	ExpandMore as ExpandMoreIcon,
	ExpandLess as ExpandLessIcon,
	Build as BuildIcon,
	CheckCircle as CheckCircleIcon,
	Error as ErrorIcon,
	Psychology as PsychologyIcon,
	ContentCopy as CopyIcon,
	ThumbUp as ThumbUpIcon,
	ThumbDown as ThumbDownIcon,
} from '@mui/icons-material';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
	ApiResponseItem,
	ApiMessageItem,
	ApiToolCallItem,
	ApiToolOutputItem,
	ApiReasoningItem,
	ApiErrorItem,
	Response,
} from '../services/api';

interface ResponseTimelineProps {
	responses: Response[];
	showAllItems: boolean;
	onToggleShowAll: () => void;
	onCopyMessage?: (content: string) => void;
	onGoodResponse?: () => void;
	onBadResponse?: () => void;
}

marked.setOptions({
	gfm: true,
	breaks: true,
});

const renderMarkdown = (content: string) => {
	const rawHtml = marked.parse(content);
	const htmlString = typeof rawHtml === 'string' ? rawHtml : String(rawHtml);
	return DOMPurify.sanitize(htmlString, { USE_PROFILES: { html: true } });
};

const getMessageStyling = (role: string) => {
	switch (role) {
		case 'user':
			return {
				justifyContent: 'flex-end',
				maxWidth: '70%',
				marginLeft: 'auto',
				backgroundColor: '#e3f2fd', // Light blue for user messages
				borderRadius: '18px 18px 4px 18px',
				padding: '12px 16px',
				margin: '8px 0'
			};
		case 'assistant':
			return {
				justifyContent: 'flex-start',
				width: '100%',
				backgroundColor: 'transparent',
				borderRadius: '0px',
				padding: '8px 0px',
				margin: '8px 0'
			};
		case 'system':
			return {
				justifyContent: 'flex-start',
				width: '90%',
				backgroundColor: '#fff3e0', // Light orange for system messages
				borderRadius: '8px',
				padding: '12px 16px',
				margin: '8px 0'
			};
		default:
			return {
				justifyContent: 'flex-start',
				width: '85%',
				backgroundColor: '#f5f5f5',
				borderRadius: '18px 18px 18px 4px',
				padding: '12px 16px',
				margin: '8px 0'
			};
	}
};

const MessageItem: React.FC<{ 
	item: ApiMessageItem;
	onCopyMessage?: (content: string) => void;
	onGoodResponse?: () => void;
	onBadResponse?: () => void;
}> = ({ item, onCopyMessage, onGoodResponse, onBadResponse }) => {
	const content =
		typeof item.content === 'string'
			? item.content
			: item.content.map((p) => p.text).join('');

	const messageStyle = getMessageStyling(item.role);

	return (
		<ListItem sx={{ padding: 0, display: 'flex', justifyContent: messageStyle.justifyContent, maxWidth: '100%', minWidth: 0 }}>
			<Box sx={{
				...messageStyle,
				maxWidth: messageStyle.maxWidth ?? '100%',
				minWidth: 0,
				overflowWrap: 'anywhere',
				wordBreak: 'break-word',
				'& pre': { overflowX: 'auto', m: 0 },
				'& code': { whiteSpace: 'pre-wrap' },
				'& table': { display: 'block', width: '100%', overflowX: 'auto' }
			}}>
				{item.role === 'assistant' ? (
					<div
						className="markdown"
						dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
					/>
				) : (
					<div className="markdown">{content}</div>
				)}
				
				{/* Add feedback buttons below assistant messages */}
				{item.role === 'assistant' && (onCopyMessage || onGoodResponse || onBadResponse) && (
					<Box sx={{ 
						display: 'flex', 
						gap: 1, 
						mt: 1, 
						justifyContent: 'flex-start',
						alignItems: 'center'
					}}>
						{onCopyMessage && (
							<IconButton
								size="small"
								onClick={() => onCopyMessage(content)}
								sx={{ 
									color: 'text.secondary',
									'&:hover': { 
										backgroundColor: 'rgba(0, 0, 0, 0.04)',
										color: 'primary.main'
									}
								}}
								title="Copy message"
							>
								<CopyIcon fontSize="small" />
							</IconButton>
						)}
						{onGoodResponse && (
							<IconButton
								size="small"
								onClick={onGoodResponse}
								sx={{ 
									color: 'text.secondary',
									'&:hover': { 
										backgroundColor: 'rgba(0, 0, 0, 0.04)',
										color: 'success.main'
									}
								}}
								title="Good response"
							>
								<ThumbUpIcon fontSize="small" />
							</IconButton>
						)}
						{onBadResponse && (
							<IconButton
								size="small"
								onClick={onBadResponse}
								sx={{ 
									color: 'text.secondary',
									'&:hover': { 
										backgroundColor: 'rgba(0, 0, 0, 0.04)',
										color: 'error.main'
									}
								}}
								title="Bad response"
							>
								<ThumbDownIcon fontSize="small" />
							</IconButton>
						)}
					</Box>
				)}
			</Box>
		</ListItem>
	);
};

const ToolCallItem: React.FC<{ item: ApiToolCallItem }> = ({ item }) => {
	const [expanded, setExpanded] = React.useState(false);
	const argsStr =
		typeof item.arguments === 'string'
			? item.arguments
			: JSON.stringify(item.arguments, null, 2);

	return (
		<Paper
			elevation={1}
			sx={{
				mb: 1,
				p: 1.5,
				backgroundColor: '#f5f5f5',
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
				<BuildIcon fontSize="small" color="primary" />
				<Typography variant="body2" fontWeight="bold">
					{item.tool_name}
				</Typography>
				<Chip
					label={item.status}
					size="small"
					color={
						item.status === 'completed'
							? 'success'
							: item.status === 'failed'
							? 'error'
							: 'default'
					}
				/>
			</Box>
			<IconButton
				size="small"
				onClick={() => setExpanded(!expanded)}
				sx={{ mb: 1 }}
			>
				{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
			</IconButton>
			<Collapse in={expanded}>
				<Box
					sx={{
						mt: 1,
						p: 1,
						backgroundColor: '#ffffff',
						borderRadius: 1,
						fontFamily: 'monospace',
						fontSize: '0.875rem',
						overflow: 'auto',
					}}
				>
					<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{argsStr}</pre>
				</Box>
			</Collapse>
		</Paper>
	);
};

const ToolOutputItem: React.FC<{ item: ApiToolOutputItem }> = ({ item }) => {
	const [expanded, setExpanded] = React.useState(false);
	const outputStr =
		typeof item.output === 'string'
			? item.output
			: JSON.stringify(item.output, null, 2);

	return (
		<Paper
			elevation={1}
			sx={{
				mb: 1,
				p: 1.5,
				backgroundColor: '#f0f0f0',
				marginLeft: 2,
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
				<CheckCircleIcon
					fontSize="small"
					color={
						item.status === 'completed'
							? 'success'
							: item.status === 'failed'
							? 'error'
							: 'inherit'
					}
				/>
				<Typography variant="body2" fontWeight="bold">
					Output
				</Typography>
				<Chip
					label={item.status}
					size="small"
					color={
						item.status === 'completed'
							? 'success'
							: item.status === 'failed'
							? 'error'
							: 'default'
					}
				/>
			</Box>
			{item.error && (
				<Box sx={{ mb: 1, p: 1, backgroundColor: '#ffebee', borderRadius: 1 }}>
					<Typography variant="body2" color="error">
						{item.error.message}
					</Typography>
				</Box>
			)}
			{item.output && (
				<>
					<IconButton
						size="small"
						onClick={() => setExpanded(!expanded)}
						sx={{ mb: 1 }}
					>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
					<Collapse in={expanded}>
						<Box
							sx={{
								mt: 1,
								p: 1,
								backgroundColor: '#ffffff',
								borderRadius: 1,
								fontFamily: 'monospace',
								fontSize: '0.875rem',
								overflow: 'auto',
								maxHeight: '400px',
							}}
						>
							<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
								{outputStr.length > 1000
									? outputStr.substring(0, 1000) + '...'
									: outputStr}
							</pre>
						</Box>
					</Collapse>
				</>
			)}
		</Paper>
	);
};

const ReasoningItem: React.FC<{ item: ApiReasoningItem }> = ({ item }) => {
	const [expanded, setExpanded] = React.useState(false);
	const isLong = item.content.length > 200;

	return (
		<Paper
			elevation={1}
			sx={{
				mb: 1,
				p: 1.5,
				backgroundColor: '#fff9e6',
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
				<PsychologyIcon fontSize="small" color="warning" />
				<Typography variant="body2" fontWeight="bold">
					Reasoning
				</Typography>
			</Box>
			{isLong ? (
				<>
					<Typography variant="body2">
						{expanded ? item.content : item.content.substring(0, 200) + '...'}
					</Typography>
					<IconButton size="small" onClick={() => setExpanded(!expanded)}>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</>
			) : (
				<Typography variant="body2">{item.content}</Typography>
			)}
		</Paper>
	);
};

const ErrorItem: React.FC<{ item: ApiErrorItem }> = ({ item }) => {
	return (
		<Paper
			elevation={1}
			sx={{
				mb: 1,
				p: 1.5,
				backgroundColor: '#ffebee',
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
				<ErrorIcon fontSize="small" color="error" />
				<Typography variant="body2" fontWeight="bold" color="error">
					{item.error_code}
				</Typography>
				{item.recoverable && (
					<Chip label="Recoverable" size="small" color="warning" />
				)}
			</Box>
			<Typography variant="body2">{item.message}</Typography>
			{item.details && (
				<Box
					sx={{
						mt: 1,
						p: 1,
						backgroundColor: '#ffffff',
						borderRadius: 1,
						fontFamily: 'monospace',
						fontSize: '0.875rem',
					}}
				>
					<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
						{JSON.stringify(item.details, null, 2)}
					</pre>
				</Box>
			)}
		</Paper>
	);
};

const ResponseItemRenderer: React.FC<{ 
	item: ApiResponseItem;
	onCopyMessage?: (content: string) => void;
	onGoodResponse?: () => void;
	onBadResponse?: () => void;
}> = ({ item, onCopyMessage, onGoodResponse, onBadResponse }) => {
	switch (item.type) {
		case 'message':
			return <MessageItem 
				item={item} 
				onCopyMessage={onCopyMessage}
				onGoodResponse={onGoodResponse}
				onBadResponse={onBadResponse}
			/>;
		case 'tool_call':
			return <ToolCallItem item={item} />;
		case 'tool_output':
			return <ToolOutputItem item={item} />;
		case 'reasoning':
			return <ReasoningItem item={item} />;
		case 'error':
			return <ErrorItem item={item} />;
		default:
			return (
				<Box sx={{ p: 1 }}>
					<Typography variant="body2">
						Unknown item type: {(item as any).type}
					</Typography>
				</Box>
			);
	}
};

export const ResponseTimeline: React.FC<ResponseTimelineProps> = ({
	responses,
	showAllItems,
	onToggleShowAll,
	onCopyMessage,
	onGoodResponse,
	onBadResponse,
}) => {
	// Group items by response first
	const itemsByResponse = new Map<string, Array<{ item: ApiResponseItem; responseId: string }>>();
	
	for (const response of responses) {
		if (response.items) {
			const responseItems: Array<{ item: ApiResponseItem; responseId: string }> = [];
			for (const item of response.items) {
				responseItems.push({ item, responseId: response.id });
			}
			// Sort items within each response by created_at
			responseItems.sort((a, b) => a.item.created_at - b.item.created_at);
			itemsByResponse.set(response.id, responseItems);
		}
	}
	
	// Filter items per response based on showAllItems
	const filteredItemsByResponse = new Map<string, Array<{ item: ApiResponseItem; responseId: string }>>();
	
	type ItemWithResponseId = { item: ApiResponseItem; responseId: string };
	
	for (const [responseId, items] of Array.from(itemsByResponse.entries())) {
		if (showAllItems) {
			// Show all items when showAllItems is true
			filteredItemsByResponse.set(responseId, items);
		} else {
			// When showAllItems is false, only show:
			// 1. The first user message of this response
			// 2. The last assistant message of this response
			
			const messageItems = items.filter((entry: ItemWithResponseId) => entry.item.type === 'message');
			
			// Find first user message in this response
			const firstUserMessage = messageItems.find(
				(entry: ItemWithResponseId) => entry.item.type === 'message' && (entry.item as ApiMessageItem).role === 'user'
			);
			
			// Find last assistant message in this response
			const lastAssistantMessage = messageItems
				.filter((entry: ItemWithResponseId) => entry.item.type === 'message' && (entry.item as ApiMessageItem).role === 'assistant')
				.pop();
			
			// Build filtered array with only the first user and last assistant messages for this response
			const filtered: typeof items = [];
			if (firstUserMessage) {
				filtered.push(firstUserMessage);
			}
			if (lastAssistantMessage) {
				filtered.push(lastAssistantMessage);
			}
			
			filteredItemsByResponse.set(responseId, filtered);
		}
	}
	
	return (
		<Box>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 2,
				}}
			>
				<Typography variant="h6">Response Timeline</Typography>
				<Chip
					label={showAllItems ? 'Hide Agent Thinking' : 'Show Agent Thinking'}
					onClick={onToggleShowAll}
					clickable
					color={showAllItems ? 'primary' : 'default'}
				/>
			</Box>
			
			{filteredItemsByResponse.size === 0 ? (
				<Typography variant="body2" color="text.secondary">
					No items to display
				</Typography>
			) : (
				<Box>
					{Array.from(filteredItemsByResponse.entries()).map(
						([responseId, items]) => {
							const response = responses.find(r => r.id === responseId);
							return (
								<Box key={responseId} sx={{ mb: 3 }}>
									{showAllItems && (
										<Divider sx={{ mb: 2 }}>
											<Chip
												label={`Response ${responseId}`}
												size="small"
											/>
										</Divider>
									)}
									{items.map(({ item }) => (
										<ResponseItemRenderer 
											key={item.id} 
											item={item}
											onCopyMessage={onCopyMessage}
											onGoodResponse={onGoodResponse}
											onBadResponse={onBadResponse}
										/>
									))}
									{/* Display response error if present, regardless of toggle state */}
									{response?.error && (
										<Paper
											elevation={1}
											sx={{
												mb: 1,
												mt: 2,
												p: 1.5,
												backgroundColor: '#ffebee',
											}}
										>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
												<ErrorIcon fontSize="small" color="error" />
												<Typography variant="body2" fontWeight="bold" color="error">
													{response.error.code || 'Error'}
												</Typography>
											</Box>
											<Typography variant="body2">{response.error.message}</Typography>
											{response.error.details && (
												<Box
													sx={{
														mt: 1,
														p: 1,
														backgroundColor: '#ffffff',
														borderRadius: 1,
														fontFamily: 'monospace',
														fontSize: '0.875rem',
													}}
												>
													<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
														{JSON.stringify(response.error.details, null, 2)}
													</pre>
												</Box>
											)}
										</Paper>
									)}
								</Box>
							);
						}
					)}
				</Box>
			)}
		</Box>
	);
};

